import React from "react";
import { PageHeading } from "../../components/PageHeading/PageHeading";
import { PageContent } from "../../components/PageContent/PageContent";
import {
  Box,
  Button,
  HStack,
  SkeletonText,
  Table,
  Tag,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  VStack,
  Link as ChakraLink,
} from "@chakra-ui/react";
import { Card } from "../../components/Card/Card";
import { PlusIcon } from "../../styles/icons";
import { useModalCloseEffect } from "../../hooks/useModalCloseEffect";
import { ErrorMessage } from "../../components/ErrorMessage/ErrorMessage";
import { usePaginatedAdminApiRequest } from "../../hooks/usePaginatedApiRequest";
import { NewBusinessModal } from "../../components/Business/NewBusinessModal";
import { Link } from "react-router-dom";
import { CopyText } from "../../components/Utilities/CopyText";
import { useSearchInput } from "../../hooks/useSearchInput";
import { BusinessMenu } from "../../components/Business/BusinessMenu";

export const Businesses = () => {
  const { debouncedQuery, element: searchInputElement } = useSearchInput({
    placeholder: "Search Business...",
  });

  const newBusinessModal = useDisclosure();

  const {
    refresh,
    items: businesses,
    isLoading,
    isLoadingPage,
    error,
    fetchNextPage,
  } = usePaginatedAdminApiRequest(
    // Request the first page
    (apiClient) => apiClient.getBusinesses(debouncedQuery),
    // Request subsequent pages
    (apiClient, _, continuationToken) =>
      apiClient.getBusinesses(debouncedQuery, undefined, continuationToken),
    [debouncedQuery]
  );

  // Refresh the business list when the modal is closed.
  useModalCloseEffect(newBusinessModal, () => refresh(), [refresh]);

  const skeletonLoading = (rows: number = 12) => {
    return (
      <HStack width="100%" boxShadow="lg">
        <Box width="15%" padding="4" pr="0">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="75%" padding="4" pr="0">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
        <Box width="10%" pt="4" pb="4">
          <SkeletonText mt="4" noOfLines={rows} spacing="4" />
        </Box>
      </HStack>
    );
  };

  const skeletonLoadingTable = (rows: number = 12) => {
    return (
      <Tr>
        <Td width="15%">
          <SkeletonText noOfLines={12} spacing="4" width="100%" />
        </Td>
        <Td width="70%">
          <SkeletonText noOfLines={12} spacing="4" width="100%" />
        </Td>
        <Td width="10%" whiteSpace="nowrap">
          <SkeletonText noOfLines={12} spacing="4" width="100%" />
        </Td>
        <Td width="5%" whiteSpace="nowrap">
          <SkeletonText noOfLines={12} spacing="4" width="100%" />
        </Td>
      </Tr>
    );
  };

  return (
    <>
      <PageHeading>
        <PageHeading.Title>Businesses</PageHeading.Title>
      </PageHeading>
      <PageContent>
        <Card width="100%">
          <VStack width="100%" spacing="4" alignItems="start">
            <HStack width="100%" justifyItems="start">
              <Box flex="1">
                <Box width="100%">{searchInputElement}</Box>
              </Box>
              <Button
                leftIcon={<PlusIcon />}
                onClick={newBusinessModal.onOpen}
                colorScheme="cherryButton"
              >
                New Business
              </Button>
            </HStack>
            <Table size="sm">
              <Thead>
                <Tr>
                  <Th>Business Id</Th>
                  <Th>Name</Th>
                  <Th>Status</Th>
                  <Th></Th>
                </Tr>
              </Thead>
              <Tbody>
                {isLoading && skeletonLoadingTable()}
                {!isLoading && businesses && businesses.length === 0 && (
                  <Tr>
                    <Td colSpan={4} textAlign="center">
                      No businesses found matching query '{debouncedQuery}'
                    </Td>
                  </Tr>
                )}
                {businesses &&
                  businesses.length > 0 &&
                  !isLoading &&
                  businesses?.map((business) => (
                    <Tr key={business.Meta!.Id}>
                      <Td title="Edit business." w="12%">
                        <CopyText
                          text={business.Meta?.Id!}
                          hover={true}
                          copyOnIcon={true}
                        >
                          <ChakraLink
                            as={Link}
                            to={`/admin/businesses/${business.Meta?.Id}`}
                          >
                            {business.Meta?.Id}
                          </ChakraLink>
                        </CopyText>
                      </Td>
                      <Td>
                        <ChakraLink
                          as={Link}
                          to={`/admin/businesses/${business.Meta?.Id}`}
                        >
                          {business.DisplayName}
                        </ChakraLink>
                      </Td>
                      <Td width="0.1%" whiteSpace="nowrap">
                        <Tag
                          size="sm"
                          bgColor={business.IsEnabled ? "" : "cherryButton.500"}
                          color={business.IsEnabled ? "" : "white"}
                        >
                          {business.IsEnabled ? "Enabled" : "Disabled"}
                        </Tag>
                      </Td>
                      <Td width="0.1%" whiteSpace="nowrap">
                        <BusinessMenu
                          businessId={business.Meta?.Id!}
                          onlyEllipsis
                          iconBtnProps={{
                            "aria-label": "options",
                            size: "xs",
                            colorScheme: undefined,
                          }}
                        />
                      </Td>
                    </Tr>
                  ))}
              </Tbody>
            </Table>
            {fetchNextPage && !isLoading && (
              <Box w="100%">
                {!isLoadingPage && (
                  <Button colorScheme="cherryButton" onClick={fetchNextPage}>
                    Load more...
                  </Button>
                )}
                {isLoadingPage && skeletonLoading(4)}
              </Box>
            )}
          </VStack>
        </Card>
        {error != null && (
          <ErrorMessage>
            An error was encountered while searching businesses.
          </ErrorMessage>
        )}
      </PageContent>
      <NewBusinessModal {...newBusinessModal} />
    </>
  );
};
